import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Book as BookIcon,
  Support as SupportIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../../store/hooks';
import { selectSignup } from '../../store/signupSlice';
import { marketingApi } from '../../api/marketing';
import Logo from '../../components/common/Logo';

const WelcomePage: React.FC = () => {
  const { companyName } = useAppSelector(selectSignup);
  const [instanceInfo, setInstanceInfo] = useState<{
    subdomain: string;
    status: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInstanceInfo();
  }, []);

  const loadInstanceInfo = async () => {
    try {
      const response = await marketingApi.getInstanceStatus();
      setInstanceInfo(response);
    } catch (err) {
      console.error('Failed to load instance info');
    } finally {
      setLoading(false);
    }
  };

  const resources = [
    {
      icon: BookIcon,
      title: 'Getting Started Guide',
      description: 'Learn the basics of Debtornator and set up your first collection workflow.',
      action: 'Read Guide',
      href: 'https://docs.debtornator.com/getting-started',
    },
    {
      icon: SchoolIcon,
      title: 'Video Tutorials',
      description: 'Watch step-by-step videos covering all major features.',
      action: 'Watch Videos',
      href: 'https://docs.debtornator.com/tutorials',
    },
    {
      icon: SupportIcon,
      title: 'Support Center',
      description: 'Get help from our team or search our knowledge base.',
      action: 'Get Help',
      href: 'https://support.debtornator.com',
    },
  ];

  const quickStartSteps = [
    'Import your first batch of accounts',
    'Configure your phone system',
    'Set up letter templates',
    'Add your team members',
    'Configure compliance settings',
  ];

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ mb: 3 }}>
            <Logo size="large" />
          </Box>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Welcome to Debtornator!
          </Typography>
          <Typography variant="h5" color="text.secondary">
            {companyName}, your account is ready to go.
          </Typography>
        </Box>

        {/* Instance Info Card */}
        <Card
          sx={{
            mb: 6,
            background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(244, 143, 177, 0.1) 100%)',
            border: '1px solid rgba(100, 181, 246, 0.3)',
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your Debtornator Instance
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 3,
              }}
            >
              {instanceInfo?.subdomain || 'your-company'}.debtornator.com
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<LaunchIcon />}
              href={`https://${instanceInfo?.subdomain || 'your-company'}.debtornator.com`}
              target="_blank"
              sx={{ px: 4 }}
            >
              Launch Debtornator
            </Button>
          </CardContent>
        </Card>

        <Grid container spacing={4}>
          {/* Quick Start */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Quick Start Checklist
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Complete these steps to get the most out of Debtornator
                </Typography>
                <List>
                  {quickStartSteps.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Box>
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Helpful Resources
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Everything you need to become a Debtornator expert
                </Typography>
                <List>
                  {resources.map((resource, index) => (
                    <React.Fragment key={index}>
                      <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ mt: 0.5 }}>
                          <resource.icon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={resource.title}
                          secondary={resource.description}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          href={resource.href}
                          target="_blank"
                        >
                          {resource.action}
                        </Button>
                      </ListItem>
                      {index < resources.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer CTA */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Need help getting started? Our team is here for you.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" href="mailto:support@debtornator.com">
              Contact Support
            </Button>
            <Button variant="outlined" href="https://calendly.com/debtornator/onboarding">
              Schedule Onboarding Call
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomePage;
